import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { OutlinedInput } from '@mui/material';
import { styled } from '@mui/system';

import { SearchBarProps } from './types';

const SearchBar: React.FC<SearchBarProps> = ({
  disabled,
  onClear,
  size = 'large',
  value,
  ...rest
}) => {
  const isLarge = size === 'large';

  return (
    <StyledInput
      disabled={disabled}
      endAdornment={
        value && onClear ? (
          <StyledCloseButton
            disabled={disabled}
            aria-label='Clear search'
            onClick={onClear}
            sx={{
              '&:disabled': {
                cursor: 'auto',
              },
            }}
          >
            <StyledCloseIcon />
          </StyledCloseButton>
        ) : null
      }
      fullWidth
      startAdornment={<StyledSearchIcon />}
      inputProps={{
        sx: {
          borderRadius: '60px',
          fontSize: 14,
          padding: `${isLarge ? 10 : 6}px 0`,
          paddingLeft: '6px',
        },
      }}
      isLarge={isLarge}
      value={value}
      {...rest}
      // It's a workaround for a cryptic TypeScript error.
      // Related issue: https://github.com/mui/material-ui/issues/29900
      sx={Array.isArray(rest.sx) ? rest.sx : [rest.sx]}
    />
  );
};

const StyledInput = styled(OutlinedInput, {
  shouldForwardProp: propName => propName !== 'isLarge',
})<{ isLarge: boolean }>`
  input {
    &::placeholder {
      opacity: 1 !important;
      color: ${props => props.theme.palette.colors.grey[600]} !important;
    }
  }

  background-color: ${props =>
    props.isLarge
      ? props.theme.palette.colors.slate[100]
      : props.theme.palette.colors.white};
  border-radius: ${props => (props.isLarge ? '60px' : '4px')};
  color: ${props => props.theme.palette.colors.grey[600]};

  > fieldset {
    border: 1px solid
      ${props =>
        props.isLarge ? 'transparent' : props.theme.palette.colors.grey[500]};
  }

  &:hover:not(&.Mui-focused) > fieldset {
    border-color: ${props => props.theme.palette.colors.purple[600]};
  }

  &:hover:not(:disabled),
  &.Mui-focused {
    background-color: ${props => props.theme.palette.colors.white};
    color: ${props => props.theme.palette.colors.black};

    > fieldset {
      border-color: ${props => props.theme.palette.colors.purple[600]};
      outline: none;
    }
  }
`;

const StyledSearchIcon = styled(SearchIcon)`
  height: 18px;
  width: 18px;
`;

const StyledCloseButton = styled('button')`
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  height: 18px;
  width: 18px;
`;

const StyledCloseIcon = styled(CloseIcon)`
  height: 18px;
  width: 18px;
`;

export default SearchBar;
